import Glide from '@glidejs/glide';
import NativePlant from '../../types/NativePlant';
import {
	NativePlantSearchFilterFormField,
	NativePlantSearchFilterFormFieldOption
} from '../../types/NativePlantSearchFilterFormField';
import NativePlantSearchLocalStorage from '../../types/NativePlantSearchLocalStorage';
import SearchUtil from '../../utils/search';
import Modal from 'bootstrap/js/dist/modal.js';


const anyValue: string                             = '__any__';
const noDataValue: string                          = 'noData';
const searchThreshold: number                      = 0.3;
let plants: NativePlant[]                          = [];
let filteredPlants: NativePlant[]                  = [];
let itemListTemplate: HTMLTemplateElement | null   = null;
let itemDetailTemplate: HTMLTemplateElement | null = null;
let itemsPerPage: number                           = 10;
let currentPage: number                            = 1;
let maxCommonNames: number                         = 0;


/**
 *
 */
const setupNativePlantSearch = () =>
{
	const plantJsonElem: HTMLScriptElement | null = document.querySelector( '#native-plants-json' );

	if (!(plantJsonElem instanceof HTMLScriptElement)) {
		return;
	}

	const plantJsonArr = JSON.parse( plantJsonElem.text );

	if (!Array.isArray( plantJsonArr )) {
		console.warn( 'error parsing #native-plants-json', plantJsonArr );
		return;
	}

	plants = plantJsonArr;

	const storage = getLocalPlantStorage();

	filteredPlants     = [...plants];
	itemListTemplate   = document.querySelector( "#plant-search-item-list-template" );
	itemDetailTemplate = document.querySelector( "#plant-search-item-detail-template" );

	plants.forEach( plant =>
	{
		plant.saved = storage.savedPlantIds.indexOf( plant.id ) !== -1;

		maxCommonNames = Math.max( maxCommonNames, plant.commonNames.length );
	} );

	setupJumpLink();
	populateFilterForm();
	setupPageControls();
	filterAndSortPlants();
	draw();
};


/**
 *
 */
const setupJumpLink = () =>
{
	const jumpLink = document.querySelector( '#native-plants-jump-link' );
	const section  = document.querySelector( '.section-native-plant-search' );
	const options  = {
		rootMargin: "0px 0px -300px 0px",
	};

	if (!(section instanceof HTMLElement)) {
		return;
	}

	const callback: IntersectionObserverCallback = ( entries ) =>
	{
		entries.forEach( ( entry ) =>
		{
			if (jumpLink instanceof HTMLButtonElement) {
				if (entry.isIntersecting) {
					jumpLink.classList.add( 'hide' );
				} else {
					jumpLink.classList.remove( 'hide' );
				}
			}
		} );
	};

	const observer = new IntersectionObserver( callback, options );

	observer.observe( section );

	if (jumpLink instanceof HTMLButtonElement) {
		jumpLink.addEventListener( 'click', e =>
		{
			e.preventDefault();

			if (section instanceof HTMLElement) {
				section.scrollIntoView();
			}
		} );
	}
};


/**
 *
 */
const draw = () =>
{
	drawList();
	drawSavedList();

	// view button event handlers

	const detailButtons: NodeListOf<HTMLElement> = document.querySelectorAll( '[data-view-plant-detail]' );

	detailButtons.forEach( button =>
	{
		if (!(button instanceof HTMLElement)) {
			return;
		}

		button.addEventListener( 'click', () =>
		{
			const modalElem: HTMLDivElement | null = document.querySelector( '#plant-search-item-detail-modal' );
			const plantId: number                  = parseInt( button.dataset.plantId || '0', 10 );
			const plant: NativePlant | undefined   = plants.find( plant => plant.id === plantId );

			if (!plant || !itemDetailTemplate || !(modalElem instanceof HTMLDivElement)) {
				return;
			}

			// modal
			
			modalElem.innerHTML = `
				<div class="modal-dialog modal-xl modal-dialog-scrollable">
					<div class="modal-content">
						<div class="modal-header">
							<button class="btn btn-link close-button" type="button" data-bs-dismiss="modal" aria-label="Close">
								<i class="fa-solid fa-xmark"></i>
							</button>
						</div>
						<div class="modal-body">
							${getItemHtml( plant, itemDetailTemplate )}
						</div>
					</div>
				</div>
			`;

			// move header into modal header

			const modalHeader = modalElem.querySelector( '.modal-header' );
			const bodyHeader  = modalElem.querySelector( '.item-detail-header' );

			if (modalHeader && bodyHeader) {
				modalHeader.insertAdjacentElement( 'afterbegin', bodyHeader );
			}

			const modal = new Modal( modalElem );

			modal.show();

			// slideshow

			if (plant.photos.length > 1) {

				setTimeout( () =>
				{
					const glide = new Glide( '#plant-search-item-detail-modal .glide', {
						gap:    0,
						rewind: false,
					} );

					glide.mount();
				}, 250 );
			}

			// print button

			const printButton = modalElem.querySelector( '[data-print]' );

			if (printButton instanceof HTMLButtonElement) {
				printButton.addEventListener( 'click', e =>
				{
					e.preventDefault();
					window.print();
				} );
			}

			// save buttons

			const saveButtons = modalElem.querySelectorAll( '.section-native-plant-search [data-save-plant]' );

			saveButtons.forEach( button =>
			{
				button.addEventListener( 'click', () =>
				{
					const header          = modalElem.querySelector( '.item-detail-header' );
					const detailContainer = modalElem.querySelector( '.plant-search-item-detail' );
					let savedValue        = 'true';

					if (!(header instanceof HTMLElement) || !(detailContainer instanceof HTMLElement)) {
						return;
					}

					plants.forEach( plant =>
					{
						if (plant.id === plantId) {
							plant.saved = !plant.saved;
							savedValue  = plant.saved ? 'true' : 'false';
						}
					} );

					detailContainer.setAttribute( 'data-plant-saved', savedValue );
					header.setAttribute( 'data-plant-saved', savedValue );
					savePlantsState( plants );
					draw();
				} );
			} );
		} );
	} );

	// delete button event handlers

	const deleteButtons: NodeListOf<HTMLButtonElement> = document.querySelectorAll( '.section-native-plant-search [data-delete-plant]' );

	deleteButtons.forEach( button =>
	{
		button.addEventListener( 'click', () =>
		{
			const plantId = parseInt( button.dataset.plantId || '0', 10 );

			if (confirm( 'Remove this plant from your saved list?' )) {

				plants.forEach( plant =>
				{
					if (plant.id === plantId) {
						plant.saved = false;
					}
				} );

				savePlantsState( plants );
				draw();
			}
		} );
	} );

	// saved count

	const savedCountElem = document.querySelector( '#saved-count' );
	const savedCount     = plants.filter( plant => plant.saved ).length;

	if (savedCountElem instanceof HTMLElement) {
		savedCountElem.innerHTML = `(${savedCount})`;
	}
};


/**
 *
 */
const drawList = () =>
{
	const container            = document.querySelector( '#plant-search-list' );
	const resultCountContainer = document.querySelector( '#result-count' );
	const showMoreContainer    = document.querySelector( '#plant-search-list-show-more' );

	if (!(container instanceof HTMLElement) || !(resultCountContainer instanceof HTMLElement) || !(showMoreContainer instanceof HTMLElement)) {
		return;
	}

	const showingElems: NodeListOf<HTMLElement> = container.querySelectorAll( '.plant-search-list-item.show' );
	const showingIds: number[]                  = [...showingElems].map( elem => parseInt( elem.dataset.plantId || '0', 10 ) );
	const initialContainerHeight: number        = container.clientHeight;
	const limit: number                         = itemsPerPage * currentPage;
	let html: string                            = '';

	container.style.minHeight = `${initialContainerHeight}px`;

	if (filteredPlants.length) {

		filteredPlants.forEach( ( plant, key ) =>
		{
			if (key < limit) {

				const classes = ['search-list'];

				if (showingIds.indexOf( plant.id ) !== -1) {
					classes.push( 'show' );
				}

				if (itemListTemplate) {
					html += getItemHtml( plant, itemListTemplate, classes );
				}
			}
		} );

	} else {

		html += `<p class="no-results">No plants match the given criteria.</p>`;
	}

	if (filteredPlants.length <= limit) {
		showMoreContainer.classList.add( 'hidden' );
	} else {
		showMoreContainer.classList.remove( 'hidden' );
	}

	container.innerHTML            = html;
	resultCountContainer.innerHTML = `(${filteredPlants.length})`;

	setTimeout( () =>
	{
		const items = container.querySelectorAll( '.plant-search-list-item' );

		items.forEach( item =>
		{
			if (!item.classList.contains( 'show' )) {
				item.classList.add( 'show' );
			}
		} );
	}, 10 );

	setTimeout( () => container.style.minHeight = `auto`, 500 );
};


/**
 *
 */
const drawSavedList = () =>
{
	const container      = document.querySelector( '#saved-plants-list' );
	const headerControls = document.querySelector( '#saved-tab-pane .header-controls' );
	const savedPlants    = plants.filter( plant => plant.saved );
	let html             = '';

	if (!(container instanceof HTMLElement) || !(headerControls instanceof HTMLElement)) {
		return;
	}

	if (savedPlants.length) {

		html += savedPlants
			.map( ( plant ) =>
			{
				if (itemListTemplate instanceof HTMLTemplateElement) {
					return getItemHtml( plant, itemListTemplate );
				}

				return '';
			} )
			.join( '' );

		headerControls.classList.remove( 'hidden' );

	} else {

		html += `<p class="no-results">No saved plants.</p>`;

		headerControls.classList.add( 'hidden' );
	}

	container.innerHTML = html;
};


/**
 *
 */
const filterAndSortPlants = () =>
{
	currentPage = 1;

	// filter

	const formFieldGroups: NodeListOf<HTMLElement>   = document.querySelectorAll( '#plant-search-filter-form .field .dropdown-menu' );
	const fields: NativePlantSearchFilterFormField[] = getFilterFormFields();
	const excludedIds: number[]                      = [];
	let filtersActive: boolean                       = false;

	const excludeBySelected = ( field: NativePlantSearchFilterFormField, selectedOptions: (string | number)[] ) =>
	{
		plants.forEach( plant =>
		{
			if (excludedIds.indexOf( plant.id ) === -1) {
				if (!field.test( plant, selectedOptions )) {
					excludedIds.push( plant.id );
				}
			}
		} );
	};

	formFieldGroups.forEach( group =>
	{
		const fieldType: string | undefined                       = group.dataset.fieldType;
		const fieldId: string | undefined                         = group.dataset.fieldId;
		const field: NativePlantSearchFilterFormField | undefined = fields.find( field => field.id === fieldId );
		let isActive: boolean                                     = false;

		if (fieldType === 'checkboxes') {

			const checkedChecks: NodeListOf<HTMLInputElement> = group.querySelectorAll( '[type="checkbox"]:checked' );
			const anyChecked                                  = group.querySelector( `[value="${anyValue}"]:checked` );
			const selectedOptions                             = [...checkedChecks].map( check => check.value );

			if (!anyChecked && field) {
				excludeBySelected( field, selectedOptions );
			}

			isActive = !Boolean( anyChecked );

		} else if (fieldType === 'selectRange') {

			const selects: NodeListOf<HTMLSelectElement> = group.querySelectorAll( 'select' );
			const defaultValues                          = [...selects].map( select => select.dataset.defaultValue );
			const selectedOptions                        = [...selects].map( select => select.value );

			if (field) {
				excludeBySelected( field, selectedOptions );
			}

			isActive = selectedOptions[0] !== defaultValues[0] || selectedOptions[1] !== defaultValues[1];

		} else if (fieldType === 'rangeRange') {

			const ranges: NodeListOf<HTMLInputElement> = group.querySelectorAll( '[type="range"]' );
			const defaultValues                        = [...ranges].map( select => select.dataset.defaultValue );
			const selectedOptions                      = [...ranges].map( input => input.value );

			if (field) {
				excludeBySelected( field, selectedOptions );
			}

			isActive = selectedOptions[0] !== defaultValues[0] || selectedOptions[1] !== defaultValues[1];
		}

		const fieldElem = group.closest( '.field' );

		if (fieldElem) {
			fieldElem.setAttribute( 'data-filter-active', isActive ? 'true' : 'false' );
		}

		if (isActive) {
			filtersActive = true;
		}
	} );

	// search

	const searchField = document.querySelector( '#filter-plant-search-input' );

	if (!(searchField instanceof HTMLInputElement)) {
		return;
	}

	const searchText       = searchField.value.trim();
	const searchProperties = ['title'];
	const searchList       = plants.map( plant =>
	{
		const listItem = { id: plant.id, title: plant.title };

		for (let i = 0; i < maxCommonNames; i++) {
			listItem[`commonName${i}`] = plant.commonNames[i] || '';
		}

		return listItem;
	} );

	for (let i = 0; i < maxCommonNames; i++) {
		searchProperties.push( `commonName${i}` );
	}

	if (searchText.length >= 2) {

		const results = SearchUtil.search(
			searchList,
			searchText,
			searchProperties,
			searchThreshold,
			// true
		);

		const searchResultIds = results.map( r => r.id );

		plants.forEach( plant =>
		{
			if (excludedIds.indexOf( plant.id ) === -1) {
				if (searchResultIds.indexOf( plant.id ) === -1) {
					excludedIds.push( plant.id );
				}
			}
		} );

		searchField.classList.add( 'active' );

		filtersActive = true;

	} else {

		searchField.classList.remove( 'active' );
	}


	filteredPlants = plants.filter( plant => excludedIds.indexOf( plant.id ) === -1 );

	// sort

	const sortSelect = document.querySelector( '#plant-search-sort' );

	if (sortSelect instanceof HTMLSelectElement) {
		sortPlants( filteredPlants, sortSelect.value );
	}

	// clear button

	const clearButton = document.querySelector( '[data-clear-filters]' );

	if (clearButton instanceof HTMLButtonElement) {
		if (filtersActive) {
			clearButton.removeAttribute( 'disabled' );
		} else {
			clearButton.setAttribute( 'disabled', 'disabled' );
		}
	}
};


/**
 *
 */
const sortPlants = ( plants: NativePlant[], sortMethod: string ) =>
{
	switch ( sortMethod ) {
		case 'alpha-asc':
			plants.sort( ( a, b ) =>
			{
				return a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1;
			} );
			break;
		case 'alpha-desc':
			plants.sort( ( a, b ) =>
			{
				return a.title.toLowerCase() > b.title.toLowerCase() ? -1 : 1;
			} );
			break;
		case 'landscape-use-asc':
			plants.sort( ( a, b ) =>
			{
				return a.landscapingUse.label.toLowerCase() > b.landscapingUse.label.toLowerCase() ? 1 : -1;
			} );
			break;
		case 'size-asc':
			plants.sort( ( a, b ) =>
			{
				if (a.size.minimum === b.size.minimum) {
					return a.size.maximum > b.size.maximum ? 1 : -1;
				}

				return a.size.minimum > b.size.minimum ? 1 : -1;
			} );
			break;
		case 'size-desc':
			plants.sort( ( a, b ) =>
			{
				if (a.size.minimum === b.size.minimum) {
					return a.size.maximum > b.size.maximum ? -1 : 1;
				}

				return a.size.maximum > b.size.maximum ? -1 : 1;
			} );
			break;
	}
};


/**
 *
 */
const populateFilterForm = () =>
{
	const formContainer: Element | null              = document.querySelector( '#plant-search-filter-form .field-container' );
	const fields: NativePlantSearchFilterFormField[] = getFilterFormFields();
	let html: string                                 = '';

	if (!(formContainer instanceof HTMLElement)) {
		return;
	}

	fields.forEach( field =>
	{
		const filterTemplate = document.querySelector( '#plant-search-filter-template' );

		if (!(filterTemplate instanceof HTMLTemplateElement)) {
			return;
		}

		const clone     = filterTemplate.content.cloneNode( true );
		const container = document.createElement( 'div' );
		const filterId  = `plant-search-search-filter-${field.id}`;

		container.append( clone );

		const dropdownButton        = container.querySelector( 'button' );
		const dropdownButtonText    = container.querySelector( 'button .text' );
		const dropdownPane          = container.querySelector( '.dropdown-menu' );
		const fieldOptionsContainer = container.querySelector( '.field-options' );
		let fieldOptionsHtml        = '';

		if (!(fieldOptionsContainer instanceof HTMLElement)
			|| !(dropdownPane instanceof HTMLElement)
			|| !(dropdownButton instanceof HTMLElement)
			|| !(dropdownButtonText instanceof HTMLElement)) {
			return;
		}

		dropdownPane.id = filterId;
		dropdownPane.setAttribute( 'data-field-type', field.type );
		dropdownPane.setAttribute( 'data-field-id', field.id );
		dropdownButton.setAttribute( 'data-toggle', filterId );
		dropdownButtonText.innerHTML = field.name;

		if (field.type === 'checkboxes') {

			field.options.forEach( option =>
			{
				const id             = `filter-option-${field.id}-${option.value}`;
				const containerClass = option.value === anyValue ? 'any' : '';

				fieldOptionsHtml += `
					<div class="checkbox-container form-check ${containerClass}">
						<input id="${id}" 
								class="form-check-input"
								type="checkbox" ${option.selected ? 'checked' : ''}
								value="${_escapeAttr( option.value )}"
						>
						<label for="${id}" class="form-check-label">
							${option.color ? `
								<span class="color-swatch" style="background: ${option.color}"></span>
							` : ''}
							<span>
								${option.label}
							</span>
						</label>
					</div>
				`;
			} );

		} else if (field.type === 'select') {

			let selectOptionHtml: string            = '';
			let selectDefaultValue: string | number = '';

			field.options.forEach( option =>
			{
				selectOptionHtml += `
							<option value="${_escapeAttr( option.value )}" ${option.selected ? 'selected' : ''}>
								${option.label}
							</option>
						`;

				if (option.selected) {
					selectDefaultValue = option.value;
				}
			} );

			fieldOptionsHtml += `
				<div class="select-container">
					<select id="filter-select-${field.id}"
							data-default-value="${selectDefaultValue}"
							class="form-select"
					>
						${selectOptionHtml}
					</select>
				</div>
			`;

		} else if (field.type === 'selectRange') {

			let selectOption1Html: string            = '';
			let selectOption2Html: string            = '';
			let select1DefaultValue: string | number = '';
			let select2DefaultValue: string | number = '';

			field.options.forEach( option =>
			{
				selectOption1Html += `
					<option value="${_escapeAttr( option.value )}" ${option.selected ? 'selected' : ''}>
						${option.label}
					</option>
				`;

				if (option.selected) {
					select1DefaultValue = option.value;
				}
			} );

			if (field.options2) {
				field.options2.forEach( option =>
				{
					selectOption2Html += `
						<option value="${_escapeAttr( option.value )}" ${option.selected ? 'selected' : ''}>
							${option.label}
						</option>
					`;

					if (option.selected) {
						select2DefaultValue = option.value;
					}
				} );
			}

			fieldOptionsHtml += `
				<div class="select-range-container">
					<select id="filter-select-${field.id}-1" data-default-value="${select1DefaultValue}" class="form-select">
						${selectOption1Html}
					</select>
					<span>
						to
					</span>
					<select id="filter-select-${field.id}-2" data-default-value="${select2DefaultValue}" class="form-select">
						${selectOption2Html}
					</select>
				</div>
			`;

		} else if (field.type === 'rangeRange' && field.options2) {

			const selected1     = field.options.find( o => o.selected );
			const selected2     = field.options2.find( o => o.selected );
			const defaultValue1 = selected1?.value || '0';
			const defaultValue2 = selected2?.value || '0';

			if (selected1 && selected2) {
				fieldOptionsHtml += `
					<div class="range-range-container">
						<div class="range-1">
							<label for="filter-range-${field.id}-1" class="form-label">
								Minimum:
								<span id="filter-range-${field.id}-value-1">
									${selected1.label}
								</span>
							</label>
							<input
								id="filter-range-${field.id}-1"
								class="form-range"
								type="range" 
								min="${field.options[0].value}" 
								max="${field.options.at( -1 )?.value}" 
								value="${defaultValue1}"
								data-update-id="filter-range-${field.id}-value-1"
								data-options-prop="options"
								data-default-value="${defaultValue1}"
							>
						</div>
						<div class="range-1">
							<label for="filter-range-${field.id}-2" class="form-label">
								Maximum:
								<span id="filter-range-${field.id}-value-2">
									${selected2.label}
								</span>
							</label>
							<input
								id="filter-range-${field.id}-2"
								class="form-range"
								type="range" 
								min="${field.options2[0].value}" 
								max="${field.options2.at( -1 )?.value}" 
								value="${defaultValue2}"
								data-update-id="filter-range-${field.id}-value-2"
								data-options-prop="options2"
								data-default-value="${defaultValue2}"
							>
						</div>
					</div>
				`;
			}
		}

		fieldOptionsContainer.innerHTML = fieldOptionsHtml;

		html += container.innerHTML;
	} );

	// add search

	html += `
		<div class="field search-field">
			<span class="search-icon"><i class="fa-solid fa-magnifying-glass"></i></span>
			<input type="search" id="filter-plant-search-input" class="form-control" placeholder="Search Name(s)">
			<button class="btn btn-text" data-clear-search aria-label="Clear Search">
				<i class="fa-solid fa-xmark"></i>
			</button>
		</div>
	`;

	formContainer.innerHTML = html;

	// search event handlers

	const searchField       = formContainer.querySelector( '#filter-plant-search-input' );
	const clearSearchButton = formContainer.querySelector( '.search-field [data-clear-search]' );
	const filterAndDraw     = () =>
	{
		filterAndSortPlants();
		draw();
	};

	if (searchField instanceof HTMLInputElement && clearSearchButton instanceof HTMLButtonElement) {

		const url             = new URL( window.location.href );
		const params          = new URLSearchParams( url.search );
		const selectedPlantId = parseInt( params.get( 'plantId' ) || '', 10 );

		if (selectedPlantId) {
			const selectedPlant = plants.find( plant => plant.id === selectedPlantId );

			if (selectedPlant) {
				searchField.value = selectedPlant.title;

				searchField.classList.add( 'active' );
			}
		}

		searchField.addEventListener( 'keyup', filterAndDraw );

		clearSearchButton.addEventListener( 'click', e =>
		{
			searchField.value = '';

			e.preventDefault();
			filterAndDraw();
		} );
	}

	// filter event handlers

	const formElements: NodeListOf<HTMLInputElement | HTMLSelectElement> = formContainer.querySelectorAll( 'input, select' );

	formElements.forEach( elem =>
	{
		const dropdown = elem.closest( '.dropdown-menu' );

		if (!(dropdown instanceof HTMLElement)) {
			return;
		}

		const fieldId = dropdown.dataset.fieldId;
		const field   = fields.find( field => field.id === fieldId );

		if (!field) {
			return;
		}

		elem.addEventListener( 'change', () =>
		{
			const isAny = elem.value === anyValue;

			if (field.type === 'checkboxes') {

				if (isAny) {

					resetFilterField( field );

				} else {

					const anyCheckbox = dropdown.querySelector( `[value="${anyValue}"]` );

					if (anyCheckbox instanceof HTMLInputElement) {
						anyCheckbox.checked = false;
					}
				}
			}

			filterAndSortPlants();
			draw();
		} );

		if (field.type === 'rangeRange') {

			elem.addEventListener( 'input', ( event ) =>
			{
				const target = event.target;

				if (target instanceof HTMLInputElement) {

					const updateId       = elem.dataset.updateId;
					const optionsProp    = elem.dataset.optionsProp || '';
					const updateElem     = dropdown.querySelector( '#' + updateId );
					const value          = parseInt( target.value, 10 );
					const selectedOption = field[optionsProp].find( ( option: NativePlantSearchFilterFormFieldOption ) =>
					{
						return option.value === value;
					} );

					if (selectedOption && updateElem instanceof HTMLElement) {
						updateElem.innerHTML = `${selectedOption.label}`;
					}
				}
			} );
		}
	} );

	// sort event handler

	const sortSelect = document.querySelector( '#plant-search-sort' );

	if (sortSelect instanceof HTMLSelectElement) {
		sortSelect.addEventListener( 'change', () =>
		{
			filterAndSortPlants();
			draw();
		} );
	}

	// dropdown reset/close event handlers

	const closeButtons: NodeListOf<HTMLButtonElement> = document.querySelectorAll( '[data-close-dropdown]' );
	const resetButtons: NodeListOf<HTMLButtonElement> = document.querySelectorAll( '[data-reset-filter-field]' );

	resetButtons.forEach( button =>
	{
		button.addEventListener( 'click', e =>
		{
			const dropdown  = button.closest( '.dropdown-menu' );
			const fieldElem = button.closest( '.field' );

			if (dropdown instanceof HTMLElement && fieldElem instanceof HTMLElement) {

				const fieldId = dropdown.dataset.fieldId;
				const field   = fields.find( field => field.id === fieldId );

				e.preventDefault();

				if (field) {
					resetFilterField( field );
					fieldElem.setAttribute( 'data-filter-active', 'false' );
					filterAndSortPlants();
					draw();
				}
			}
		} );
	} );

	closeButtons.forEach( button =>
	{
		button.addEventListener( 'click', e =>
		{
			e.preventDefault();

			const dropdown = button.closest( '.dropdown' );

			if (dropdown instanceof HTMLElement) {

				const toggleBtn = dropdown.querySelector( '.dropdown-toggle' );

				if (toggleBtn instanceof HTMLButtonElement) {
					toggleBtn.click();
				}
			}
		} );
	} );

	// clear button

	const clearButton = document.querySelector( '[data-clear-filters]' );

	if (clearButton instanceof HTMLButtonElement) {
		clearButton.addEventListener( 'click', () =>
		{
			const searchInput = document.querySelector( '#filter-plant-search-input' );

			if (searchInput instanceof HTMLInputElement) {
				searchInput.value = '';
				searchInput.classList.remove( 'active' );
			}

			fields.forEach( field => resetFilterField( field ) );
			filterAndSortPlants();
			draw();
		} );
	}


	// mobile close button

	const mobileCloseButton = document.querySelector( '[data-close-filter-form]' );

	if (mobileCloseButton instanceof HTMLButtonElement) {
		mobileCloseButton.addEventListener( 'click', e =>
		{
			e.preventDefault();

			const container = mobileCloseButton.closest( '.mobile-dropdown' );

			if (container instanceof HTMLElement) {

				const toggleBtn = container.querySelector( '.dropdown-toggle' );

				if (toggleBtn instanceof HTMLButtonElement) {
					toggleBtn.click();
				}
			}
		} );
	}
};


/**
 *
 */
const resetFilterField = ( field: NativePlantSearchFilterFormField ) =>
{
	if (field.type === 'checkboxes') {

		field.options.forEach( option =>
		{
			const checkboxId = `filter-option-${field.id}-${option.value}`;
			const checkbox   = document.querySelector( '#' + checkboxId );

			if (checkbox instanceof HTMLInputElement) {
				checkbox.checked = option.selected;
			}
		} );

	} else if (field.type === 'selectRange') {

		for (let i = 1; i <= 2; i++) {

			const selectId = `filter-select-${field.id}-${i}`;
			const select   = document.querySelector( '#' + selectId );
			const options  = field[`options${i === 1 ? '' : '2'}`];

			if (options && select instanceof HTMLSelectElement) {

				const selectedOption = options.find( option => option.selected );

				select.value = (selectedOption?.value || '').toString();
			}
		}

	} else if (field.type === 'rangeRange') {

		for (let i = 1; i <= 2; i++) {

			const inputId = `filter-range-${field.id}-${i}`;
			const input   = document.querySelector( '#' + inputId );

			if (input instanceof HTMLInputElement) {
				input.value = input.dataset.defaultValue || '';
			}
		}
	}
};


/**
 *
 */
const getFilterFormFields = (): NativePlantSearchFilterFormField[] =>
{
	const sortAlpha = ( arr: NativePlantSearchFilterFormFieldOption[] ) =>
	{
		return arr.sort( ( a, b ) =>
		{
			if (a.value === anyValue && b.value !== anyValue) {
				return -1;
			} else if (a.value === noDataValue && b.value !== noDataValue) {
				return -1;
			} else if (b.value === noDataValue && a.value !== noDataValue) {
				return 1;
			}

			return a.value < b.value ? -1 : 1;
		} );
	};

	const anyField = {
		label:    'Any',
		value:    anyValue,
		selected: true,
	};

	const landscapingUseOptions: NativePlantSearchFilterFormFieldOption[]   = [anyField];
	const lifeCycleOptions: NativePlantSearchFilterFormFieldOption[]        = [anyField];
	const waterUseOptions: NativePlantSearchFilterFormFieldOption[]         = [anyField];
	const minSizeOptions: NativePlantSearchFilterFormFieldOption[]          = [];
	const maxSizeOptions: NativePlantSearchFilterFormFieldOption[]          = [];
	const bloomMonthStartOptions: NativePlantSearchFilterFormFieldOption[]  = [];
	const bloomMonthEndOptions: NativePlantSearchFilterFormFieldOption[]    = [];
	const lightRequirementOptions: NativePlantSearchFilterFormFieldOption[] = [anyField];
	const bloomColorOptions: NativePlantSearchFilterFormFieldOption[]       = [anyField];
	const pollinatorOptions: NativePlantSearchFilterFormFieldOption[]       = [anyField];
	let maxSize: number                                                     = 0;

	plants.forEach( plant =>
	{
		if (!landscapingUseOptions.find( option => option.value === plant.landscapingUse.value )) {
			if (plant.landscapingUse.value !== null) {
				landscapingUseOptions.push( {
					...plant.landscapingUse,
					selected: false
				} );
			}
		}

		if (!lifeCycleOptions.find( option => option.value === plant.lifeCycle.value )) {
			lifeCycleOptions.push( {
				...plant.lifeCycle,
				selected: false
			} );
		}

		if (!waterUseOptions.find( option => option.value === plant.waterUse.value )) {
			waterUseOptions.push( {
				...plant.waterUse,
				selected: false
			} );
		}

		if (!bloomMonthStartOptions.find( option => option.value === plant.bloomTimes.start.value )) {
			bloomMonthStartOptions.push( {
				...plant.bloomTimes.start,
				selected: false
			} );
		}

		if (!bloomMonthEndOptions.find( option => option.value === plant.bloomTimes.end.value )) {
			bloomMonthEndOptions.push( {
				...plant.bloomTimes.end,
				selected: false
			} );
		}

		plant.lightRequirements.forEach( item =>
		{
			if (!lightRequirementOptions.find( option => option.value === item.value )) {
				lightRequirementOptions.push( {
					...item,
					selected: false
				} );
			}
		} );

		plant.bloomColors.forEach( item =>
		{
			if (!bloomColorOptions.find( option => option.value === item.value )) {
				bloomColorOptions.push( {
					...item,
					color:    getBloomColor( item.value ),
					selected: false,
				} );
			}
		} );

		plant.pollinatorsWildlife.forEach( item =>
		{
			if (!pollinatorOptions.find( option => option.value === item.value )) {
				pollinatorOptions.push( {
					...item,
					selected: false,
				} );
			}
		} );

		maxSize = Math.max( plant.size.maximum, maxSize );
	} );


	landscapingUseOptions.forEach( option =>
	{
		const landscapingPlants = plants.filter( plant => plant.landscapingUse.value === option.value );
		option.minSize          = maxSize;
		option.maxSize          = 0;

		landscapingPlants.forEach( plant =>
		{
			option.minSize = Math.min( option.minSize || 0, plant.size.minimum );
			option.maxSize = Math.max( option.maxSize || 0, plant.size.maximum );
		} );
	} );

	sortAlpha( landscapingUseOptions );
	sortAlpha( lifeCycleOptions );
	sortAlpha( bloomMonthStartOptions );
	sortAlpha( bloomMonthEndOptions );
	sortAlpha( lightRequirementOptions );
	sortAlpha( waterUseOptions );
	sortAlpha( bloomColorOptions );
	sortAlpha( pollinatorOptions );

	if (bloomMonthStartOptions.length) {
		bloomMonthStartOptions[0].selected = true;
	}

	if (bloomMonthEndOptions.length) {
		const lastOption = bloomMonthEndOptions.at( -1 );

		if (lastOption) {
			lastOption.selected = true;
		}
	}

	for (let i = 0; i <= maxSize; i++) {
		if (i > 0) {
			maxSizeOptions.push( {
				value:    i,
				label:    `${i} ft`,
				selected: i === maxSize,
			} );
		}

		if (i < maxSize) {
			minSizeOptions.push( {
				value:    i,
				label:    `${i} ft`,
				selected: i === 0,
			} );
		}
	}

	return [
		{
			id:      'landscapingUse',
			name:    'Landscaping Use',
			type:    'checkboxes',
			options: landscapingUseOptions,
			test:    ( plant, selectedOptions ) =>
					 {
						 const anySelected = selectedOptions.indexOf( anyValue ) !== -1;
						 const selected    = selectedOptions.indexOf( plant.landscapingUse.value ) !== -1;

						 return anySelected || selected;
					 },
		},
		{
			id:      'lifeCycle',
			name:    'Life Cycle',
			type:    'checkboxes',
			options: lifeCycleOptions,
			test:    ( plant, selectedOptions ) =>
					 {
						 const anySelected = selectedOptions.indexOf( anyValue ) !== -1;
						 const selected    = selectedOptions.indexOf( plant.lifeCycle.value ) !== -1;

						 return anySelected || selected;
					 },
		},
		{
			id:       'size',
			name:     'Size',
			type:     'rangeRange',
			options:  minSizeOptions,
			options2: maxSizeOptions,
			test:     ( plant, selectedOptions ) =>
					  {
						  const minSize = parseInt( selectedOptions[0].toString(), 10 );
						  const maxSize = parseInt( selectedOptions[1].toString(), 10 );

						  return !(minSize > plant.size.maximum || maxSize < plant.size.minimum);
					  },
		},
		{
			id:       'bloomMonth',
			name:     'Bloom Times',
			type:     'selectRange',
			options:  bloomMonthStartOptions,
			options2: bloomMonthEndOptions,
			test:     ( plant, selectedOptions ) =>
					  {
						  const startMonthNum = parseInt( selectedOptions[0].toString(), 10 );
						  const endMonthNum   = parseInt( selectedOptions[1].toString(), 10 );

						  return !(startMonthNum > plant.bloomTimes.end.value || endMonthNum < plant.bloomTimes.start.value);
					  },
		},
		{
			id:      'lightRequirements',
			name:    'Light',
			type:    'checkboxes',
			options: lightRequirementOptions,
			test:    ( plant, selectedOptions ) =>
					 {
						 if (selectedOptions.indexOf( anyValue ) !== -1) {
							 return true;
						 }

						 return Boolean(
							 plant.lightRequirements
								 .filter( item => selectedOptions.indexOf( item.value ) !== -1 )
								 .length
						 );
					 },
		},
		{
			id:      'waterUse',
			name:    'Water Use',
			type:    'checkboxes',
			options: waterUseOptions,
			test:    ( plant, selectedOptions ) =>
					 {
						 const anySelected = selectedOptions.indexOf( anyValue ) !== -1;
						 const selected    = selectedOptions.indexOf( plant.waterUse.value.toString() ) !== -1;

						 return anySelected || selected;
					 },
		},
		{
			id:      'bloomColors',
			name:    'Bloom Color',
			type:    'checkboxes',
			options: bloomColorOptions,
			test:    ( plant, selectedOptions ) =>
					 {
						 if (selectedOptions.indexOf( anyValue ) !== -1) {
							 return true;
						 }

						 return Boolean(
							 plant.bloomColors
								 .filter( item => selectedOptions.indexOf( item.value ) !== -1 )
								 .length
						 );
					 },
		},
		{
			id:      'pollinatorsWildlife',
			name:    'Pollinators / Wildlife',
			type:    'checkboxes',
			options: pollinatorOptions,
			test:    ( plant, selectedOptions ) =>
					 {
						 const numItems: number = plant.pollinatorsWildlife.length;
						 const noData           = selectedOptions.indexOf( noDataValue ) !== -1;
						 const empty            = numItems === 0;
						 const found            = Boolean(
							 plant.pollinatorsWildlife
								 .filter( item => selectedOptions.indexOf( item.value ) !== -1 )
								 .length
						 );

						 return found || (noData && empty);
					 },
		},
	];
};


/**
 *
 */
const getItemHtml = ( plant: NativePlant, template: HTMLTemplateElement, containerClasses: string[] = [] ) =>
{
	const clone     = template.content.cloneNode( true );
	const container = document.createElement( 'div' );

	container.append( clone );

	const contentMaps = [
		{
			selector: '.name',
			content:  plant.title
		},
		{
			selector: '.common-names',
			content:  plant.commonNames.join( ', ' )
		},
		{
			selector: '.scientific-name',
			content:  plant.scientificName
		},
		{
			selector: '.life-cycle',
			content:  plant.lifeCycle.label
		},
		{
			selector: '.landscaping-use',
			content:  plant.landscapingUse.label
		},
		{
			selector: '.size',
			content:  `${plant.size.minimum.toString()}-${plant.size.maximum.toString()} ft`
		},
		{
			selector: '.bloom-time',
			content:  `${plant.bloomTimes.start.label}-${plant.bloomTimes.end.label}`
		},
		{
			selector: '.light-requirement',
			content:  plant.lightRequirements.map( item => item.label ).join( ', ' )
		},
		{
			selector: '.water-use',
			content:  plant.waterUse.label
		},
		{
			selector: '.bloom-colors',
			content:  plant.bloomColors.map( item =>
			{
				return `
					<span class="bloom-color">
						<span class="color-swatch" style="background: ${getBloomColor( item.value )}"></span>
						<span>${item.label}</span>
					</span>
				`;
			} ).join( '' )
		},
		{
			selector: '.fragrance',
			content:  plant.fragrance.label
		},
		{
			selector: '.edible-fruits',
			content:  plant.edibleFruits.label
		},
		{
			selector: '.pollinators-wildlife',
			content:  plant.pollinatorsWildlife.length ?
					  plant.pollinatorsWildlife.map( item => item.label ).join( ', ' ) :
					  '— No Data —'
		},
		{
			selector: '.larval-hosts',
			content:  plant.larvalHosts.length
					  ? plant.larvalHosts.map( item => item.label ).join( ', ' )
					  : '— No Data —'
		},
		{
			selector: '.facts .copy',
			content:  plant.interestingFacts
		},
		{
			selector: '.link-list',
			content:  plant.links.map( link => `
					<li>
						<a href="${link.url}" class="btn btn-outline-primary" target="_blank" rel="noopener">
							${link.title}
							<i class="fa-solid fa-arrow-up-right-from-square"></i>
						</a>
					</li>
				` ).join( '' )
		},
	];

	contentMaps.forEach( item =>
	{
		const elems = container.querySelectorAll( item.selector );

		elems.forEach( elem => elem.innerHTML = item.content );
	} );

	// image

	const imgElem = container.querySelector( '.photo picture img' );

	if (imgElem instanceof HTMLImageElement) {

		if (plant.photos.length) {

			imgElem.alt = plant.photos[0].alt;
			imgElem.src = `${plant.photos[0].sizes['small']}`;
			imgElem.setAttribute(
				'srcset',
				`${plant.photos[0].sizes['small']} 640w, ${plant.photos[0].sizes['medium']} 900w`
			);
		} else {
			imgElem.src = '/images/missing-plant.jpg';
		}
	}

	// slideshow

	const slideshowElem = container.querySelector( '.glide__slides' );
	const arrows        = container.querySelector( '.glide__arrows' );
	let slideshowHtml   = '';

	if (slideshowElem instanceof HTMLElement) {

		if (plant.photos.length) {

			plant.photos.forEach( photo =>
			{
				slideshowHtml += `
					<li class="glide__slide photo">
						<picture>
							<img class="orbit-image" src="${photo.sizes.medium}" alt="${photo.alt}" />
						</picture>
					</li>
				`;
			} );

		} else {
			slideshowHtml += `
				<li class="glide__slide photo">
					<picture>
						<img src="/wp-content/themes/water-is-awesome-2025/src/img/backgrounds/missing-plant.jpg" alt="" />
					</picture>
				</li>
			`;
		}

		if (plant.photos.length < 2 && arrows instanceof HTMLElement) {
			arrows.classList.add( 'hidden' );
		}

		slideshowElem.innerHTML = slideshowHtml;
	}


	// links

	const linkContainers = container.querySelectorAll( '.links' );

	if (!plant.links.length) {
		linkContainers.forEach( linkContainer =>
		{
			linkContainer.classList.add( 'hidden' );
		} );
	}

	// remove empty interesting facts

	if (!plant.interestingFacts || plant.interestingFacts.trim() === '') {

		const factsContainers = container.querySelectorAll( '.facts' );

		factsContainers.forEach( container => container.classList.add( 'hidden' ) );
	}

	// plant id attributes

	const detailButtons  = container.querySelectorAll( '[data-view-plant-detail]' );
	const deleteButtons  = container.querySelectorAll( '[data-delete-plant]' );
	const header         = container.querySelector( '.item-detail-header' );
	const innerContainer = container.firstElementChild;
	const plantId        = plant.id.toString();

	detailButtons.forEach( button => button.setAttribute( 'data-plant-id', plantId ) );
	deleteButtons.forEach( button => button.setAttribute( 'data-plant-id', plantId ) );

	if (innerContainer instanceof HTMLElement) {

		innerContainer.setAttribute( 'data-plant-id', plantId );
		innerContainer.setAttribute( 'data-plant-saved', plant.saved ? 'true' : 'false' );

		containerClasses.forEach( cssClass => innerContainer.classList.add( cssClass ) );
	}

	if (header instanceof HTMLElement) {
		header.setAttribute( 'data-plant-saved', plant.saved ? 'true' : 'false' );
	}

	return container.innerHTML;
};


/**
 *
 */
const setupPageControls = () =>
{
	// list - load more

	const showMoreButton = document.querySelector( '#plant-search-list-show-more [data-show-more]' );

	if (showMoreButton instanceof HTMLButtonElement) {
		showMoreButton.addEventListener( 'click', e =>
		{
			e.preventDefault();
			currentPage++;
			draw();
		} );
	}


	// saved list - print

	const printButton = document.querySelector( '#saved-tab-pane .controls [data-print]' );

	if (printButton instanceof HTMLButtonElement) {
		printButton.addEventListener( 'click', e =>
		{
			e.preventDefault();
			window.print();
		} );
	}

	// saved list - sort

	const sortSelect = document.querySelector( '#saved-plants-sort' );

	if (sortSelect instanceof HTMLSelectElement) {
		sortSelect.addEventListener( 'change', () =>
		{
			sortPlants( plants, sortSelect.value );
			draw();
		} );
	}
};


/**
 *
 */
const getLocalPlantStorage = (): NativePlantSearchLocalStorage =>
{
	const defaultStorage = {
		savedPlantIds: []
	};
	const storageItem    = localStorage.getItem( 'plantSearchStorage' ) || 'null';
	const savedStorage   = JSON.parse( storageItem );

	if (typeof savedStorage !== 'object') {

		return defaultStorage;

	} else {

		const filteredStorage = Object.assign( defaultStorage, savedStorage );

		Object.keys( filteredStorage ).forEach( key =>
		{
			if (!defaultStorage[key]) {
				delete filteredStorage[key];
			}
		} );

		return filteredStorage;
	}
};


/**
 *
 */
const savePlantsState = ( plants: NativePlant[] ) =>
{
	const storage = getLocalPlantStorage();

	storage.savedPlantIds = plants.filter( plant => plant.saved ).map( plant => plant.id )

	localStorage.setItem( 'plantSearchStorage', JSON.stringify( storage ) );
};


/**
 *
 */
const getBloomColors = () =>
{
	return [
		{
			slug: 'blue',
			hex:  '#56a7e1',
		},
		{
			slug: 'brown',
			hex:  '#77491b',
		},
		{
			slug: 'darkPink',
			hex:  '#ad1a4a',
		},
		{
			slug: 'green',
			hex:  '#5d770d',
		},
		{
			slug: 'orange',
			hex:  '#ed5107',
		},
		{
			slug: 'pink',
			hex:  '#f997c6',
		},
		{
			slug: 'purple',
			hex:  '#a056b5',
		},
		{
			slug: 'red',
			hex:  '#d02d3e',
		},
		{
			slug: 'white',
			hex:  '#eee',
		},
		{
			slug: 'violet',
			hex:  '#a568e1',
		},
		{
			slug: 'yellow',
			hex:  '#e2ca1a',
		},
	];
};


/**
 *
 */
const getBloomColor = ( slug: string ) =>
{
	const item = getBloomColors().find( item => item.slug === slug );

	return item ? item.hex : undefined;
};


/**
 *
 */
const _escapeAttr = ( str: string | number ): string =>
{
	const map = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		"'": '&#39;'
	};

	if (str === null) {
		return '';
	}

	return str.toString().replaceAll( /[&<>"']/g, c => map[c] );
};

/**
 *
 */
export default setupNativePlantSearch;